import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Line, ComposedChart } from "recharts";
import { useRealtimePackagePickups } from "@/hooks/dashboard/useRealtimePackagePickups";
import { Package } from "lucide-react";
import { format } from "date-fns";

interface PackagePickupTrendsChartProps {
  timeRange: string;
  refreshInterval: number;
}

const PackagePickupTrendsChart: React.FC<PackagePickupTrendsChartProps> = ({
  timeRange,
  refreshInterval
}) => {
  const { pickupData, isLoading } = useRealtimePackagePickups(timeRange, refreshInterval);

  // Intelligente chart height op basis van data points
  const chartHeight = pickupData.length > 30 ? 450 : 350;
  
  // Rotatie van X-axis labels bij veel data
  const xAxisAngle = pickupData.length > 14 ? -45 : 0;

  // Bereken dynamisch Y-axis domain voor ophaaltijd
  const pickupTimes = pickupData
    .map(d => d.avgPickupTime)
    .filter((t): t is number => typeof t === 'number' && t > 0);

  const minPickupTime = pickupTimes.length > 0 ? Math.floor(Math.min(...pickupTimes)) - 1 : 0;
  const maxPickupTime = pickupTimes.length > 0 ? Math.ceil(Math.max(...pickupTimes)) + 1 : 24;
  const yAxisDomain = pickupTimes.length > 0 ? [minPickupTime, maxPickupTime] : [0, 24];

  const chartConfig = {
    containers: {
      label: "Containers",
      color: "hsl(220 98% 61%)",
    },
    registrations: {
      label: "Gem. Ophaaltijd", 
      color: "hsl(142 76% 45%)",
    },
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Ophaal Trends</CardTitle>
            <CardDescription>Data aan het laden... ({timeRange})</CardDescription>
          </div>
          <Package className="h-4 w-4 text-muted-foreground animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!isLoading && pickupData.length === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Ophaal Trends</CardTitle>
            <CardDescription>Geen data beschikbaar</CardDescription>
          </div>
          <Package className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex flex-col items-center justify-center">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground text-center">
              Geen data beschikbaar voor de geselecteerde periode
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Ophaal Trends</CardTitle>
          <CardDescription>
            {pickupData.length} data punt{pickupData.length !== 1 ? 'en' : ''}
            {pickupData.length > 0 && 
              ` • ${pickupData[0].fullDate} tot ${pickupData[pickupData.length - 1].fullDate}`
            }
          </CardDescription>
        </div>
        <Package className="h-4 w-4 text-green-500 animate-pulse" />
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="w-full" style={{ height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart 
              data={pickupData}
              margin={{ 
                top: 20, 
                right: 30, 
                left: 20, 
                bottom: xAxisAngle === -45 ? 80 : 5 
              }}
            >
              <defs>
                <linearGradient id="containerGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartConfig.containers.color} stopOpacity={0.8}/>
                  <stop offset="100%" stopColor={chartConfig.containers.color} stopOpacity={0.3}/>
                </linearGradient>
              </defs>
              
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="hsl(var(--muted-foreground))" 
                opacity={0.3}
                vertical={false}
              />
              
              <XAxis 
                dataKey="fullDate" 
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                angle={xAxisAngle}
                textAnchor={xAxisAngle === -45 ? "end" : "middle"}
                height={xAxisAngle === -45 ? 80 : 60}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  if (timeRange === "1h" || timeRange === "4h") {
                    return format(date, "HH:mm");
                  } else if (timeRange === "24h") {
                    return format(date, "dd/MM HH:mm");
                  } else if (timeRange === "90d") {
                    return format(date, "dd MMM");
                  } else if (timeRange === "all") {
                    return format(date, "dd MMM yy");
                  }
                  return format(date, "dd/MM");
                }}
              />
              
              <YAxis 
                yAxisId="left"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                label={{ value: 'Aantal', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
              />
              
              
              <YAxis 
                yAxisId="right"
                orientation="right"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                domain={yAxisDomain}
                tickFormatter={(value) => {
                  const hours = Math.floor(value);
                  const minutes = Math.round((value - hours) * 60);
                  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                }}
                label={{ value: 'Ophaaltijd', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
              />
              
              <ChartTooltip 
                content={<ChartTooltipContent 
                  labelFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, "dd MMM yyyy");
                  }}
                  formatter={(value, name, item) => {
                    const numValue = typeof value === 'number' ? value : 0;
                    const dataKey = item && typeof item === 'object' && 'dataKey' in item ? (item as any).dataKey : undefined;
                    
                    // Containers - toon hele getallen zonder decimalen
                    if (name === 'Containers' || dataKey === 'containers') {
                      return [
                        Math.round(numValue).toString(),
                        '📦 Rolcontainers'
                      ];
                    }
                    
                    // Ophaaltijd - Nederlandse tijdweergave (14:30 uur)
                    if (name === 'Gem. Ophaaltijd' || dataKey === 'avgPickupTime') {
                      // Toon "—" voor ontbrekende data
                      if (value == null || numValue <= 0 || isNaN(numValue)) {
                        return ['—', '⏱️ Ophaaltijd'];
                      }
                      
                      let hours = Math.floor(numValue);
                      let minutes = Math.round((numValue - hours) * 60);
                      
                      // Fix rounding edge case
                      if (minutes === 60) {
                        hours += 1;
                        minutes = 0;
                      }
                      
                      return [
                        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} uur`,
                        '⏱️ Ophaaltijd'
                      ];
                    }
                    
                    return [numValue.toString(), name];
                  }}
                />} 
              />
              
              <Bar
                yAxisId="left"
                dataKey="containers"
                name="Containers"
                fill="url(#containerGradient)"
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
                animationDuration={1000}
                animationEasing="ease-out"
              />
              
              
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="avgPickupTime"
                name="Gem. Ophaaltijd"
                stroke={chartConfig.registrations.color}
                strokeWidth={4}
                dot={{ 
                  fill: 'white', 
                  stroke: chartConfig.registrations.color, 
                  strokeWidth: 3, 
                  r: 6 
                }}
                activeDot={{ 
                  r: 8, 
                  stroke: chartConfig.registrations.color, 
                  strokeWidth: 3,
                  fill: 'white',
                  filter: 'drop-shadow(0px 2px 8px rgba(34, 197, 94, 0.5))'
                }}
                animationDuration={1200}
                animationEasing="ease-out"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartContainer>
        
        {/* Enhanced Summary stats */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-border">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">
              {pickupData.reduce((sum, item) => sum + item.containers, 0)}
            </div>
            <div className="text-xs text-muted-foreground font-medium">
              Totaal Containers
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">
              {pickupData.reduce((sum, item) => sum + item.registrations, 0)}
            </div>
            <div className="text-xs text-muted-foreground font-medium">
              Totaal Registraties
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">
              {pickupData.length > 0 
                ? Math.round(pickupData.reduce((sum, item) => sum + item.containers, 0) / pickupData.length * 10) / 10
                : 0}
            </div>
            <div className="text-xs text-muted-foreground font-medium">
              Gemiddeld/Dag
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PackagePickupTrendsChart;